import { Box, IconButton, Modal, Typography, Menu, MenuItem, useTheme } from '@mui/material';
import React, { useState, useMemo, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CloseIcon from '@mui/icons-material/Close';
import eyeIcon from 'packages/novous_web/src/assets/icons/Eye.svg';
import menuIcon from 'packages/novous_web/src/assets/icons/new-menu.svg';
import OpenInFullIcon from '../../../../assets/icons/Expand.svg';
import EditIcon from '../../../../assets/icons/edit.svg';
import CollapseIcon from '../../../../assets/icons/CollapseIcon.svg';
import RestartAltIcon from '../../../../assets/icons/Reset.svg';
import ArrowLeftIcon from 'packages/novous_web/src/assets/icons/ArrowLeft.svg';
import ArrowRightIcon from 'packages/novous_web/src/assets/icons/next.svg';
import CustomDropDownInput from 'packages/novous_web/src/common/components/customDropDownInput';
import NumericalAnalysisTable from '../SafetyAnalytics/NumericalAnalysisTable';
import CommonAnalysisTable from './CommonAnalysisTable';
import EditDataModal from './EditDataModal';
import { useToast } from 'packages/novous_web/src/context/ToastContext';
import NoDataIcon from 'packages/novous_web/src/assets/icons/NoData.svg';

interface HeatmapData {
  hour: string;
  day: string;
  value: number;
  displayValue: string;
  percentage?: string; // Add percentage for tooltip
}

interface TooltipData {
  hour: string;
  day: string;
  value: string;
  percentage: string;
  x: number;
  y: number;
}

interface TableColumn {
  key: string;
  label: string;
  align?: 'left' | 'center' | 'right';
}

interface CommonHeatmapChartProps {
  title?: string;
  data?: HeatmapData[];
  containerClassName?: string;
  selectionBox?: boolean;
  getDataForSelection?: (selectedType: string | null) => HeatmapData[];
  getTitleForSelection?: (selectedType: string | null) => string;
  alertTypes?: string[];
  infoIcon?: React.ReactNode;
  tableTitle?: string;
  tableHeaderText?: string;
  tableColumns?: TableColumn[];
  tableData?: any[];
  paginationInfo?: any;
  onTableIconClick?: (selectedAlert: string | null) => void;
  onPaginationChange?: (newPage: number) => void;
  onCardDataUpdate?: (chartId?: number, startDate?: string, endDate?: string) => void;
  chartId?: number;
}

const CommonHeatmapChart: React.FC<CommonHeatmapChartProps> = ({
  title: baseTitle,
  data: initialData,
  containerClassName = '',
  selectionBox = true,
  getDataForSelection,
  getTitleForSelection,
  alertTypes = [],
  infoIcon,
  tableTitle = 'Heatmap Data Table',
  tableHeaderText = 'Detailed breakdown of events',
  tableColumns = [],
  tableData = [],
  paginationInfo,
  onTableIconClick,
  onPaginationChange,
  onCardDataUpdate,
  chartId,
}) => {
  const theme = useTheme();
  const { showToast } = useToast();
  const [tooltip, setTooltip] = useState<TooltipData | null>(null);
  const [isTableOpen, setIsTableOpen] = useState<boolean>(false);
  const [selectedAlert, setSelectedAlert] = useState<string | null>(null);
  const [isExpandedModalOpen, setIsExpandedModalOpen] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [resetTrigger, setResetTrigger] = useState<number>(0);
  const open = Boolean(anchorEl);
  const windowStart = useRef(0);
  const [dataRangeLabel, setDataRangeLabel] = useState('');
  const [hasPrevious, setHasPrevious] = useState(false);
  const [hasNext, setHasNext] = useState(false);
  const [paginationTrigger, setPaginationTrigger] = useState(0); // Trigger re-render on pagination

  // Get data based on selection if function is provided
  const data = getDataForSelection ? getDataForSelection(selectedAlert) : initialData || [];

  // Get dynamic title if function is provided
  const title = getTitleForSelection ? getTitleForSelection(selectedAlert) : baseTitle || '';

  // Define hours
  const hours = ['0-3', '4-7', '8-11', '12-15', '16-19', '20-23'];

  // Get unique days from data and sort them
  const allDays = useMemo(() => {
    // Create a map of day labels to their sort dates for proper date-based sorting
    const dayToDateMap = new Map<string, string>();
    data.forEach((d) => {
      if (!dayToDateMap.has(d.day) && (d as any).sortDate) {
        dayToDateMap.set(d.day, (d as any).sortDate);
      }
    });

    const uniqueDays = Array.from(new Set(data.map((d) => d.day))).sort((a, b) => {
      // Sort by the actual date if available, otherwise fall back to day number
      const dateA = dayToDateMap.get(a);
      const dateB = dayToDateMap.get(b);

      if (dateA && dateB) {
        return new Date(dateB).getTime() - new Date(dateA).getTime();
      }

      // Fallback to day number sorting
      const dayA = parseInt(a.split(' ')[1]);
      const dayB = parseInt(b.split(' ')[1]);
      return dayB - dayA;
    });
    return uniqueDays;
  }, [data]);

  // Pagination settings - sliding window
  const daysPerPage = 7;

  // Get current days to display (sliding window)
  const [currentDays, setCurrentDays] = useState<string[]>([]);
  const prevAllDaysLength = useRef(0);

  // Pagination handlers - sliding by one day
  const handlePrevious = useCallback(() => {
    if (hasPrevious) {
      windowStart.current = Math.max(0, windowStart.current - 1);
      setPaginationTrigger((prev) => prev + 1); // Trigger re-render
    }
  }, [hasPrevious]);

  const handleNext = useCallback(() => {
    if (hasNext) {
      windowStart.current = Math.min(allDays.length - daysPerPage, windowStart.current + 1);
      setPaginationTrigger((prev) => prev + 1); // Trigger re-render
    }
  }, [hasNext, allDays.length, daysPerPage]);

  // Single effect to handle both data changes and pagination
  useEffect(() => {
    if (allDays.length > 0) {
      // Check if allDays length changed (new data arrived)
      const allDaysLengthChanged = prevAllDaysLength.current !== allDays.length;
      prevAllDaysLength.current = allDays.length;

      // Reset window only if allDays changed
      if (allDaysLengthChanged) {
        windowStart.current = Math.max(0, allDays.length - daysPerPage);
      }

      // Always update display based on current windowStart
      const currentWindow = allDays.slice(windowStart.current, windowStart.current + daysPerPage);
      setCurrentDays(currentWindow);

      const startLabel = currentWindow[0];
      const endLabel = currentWindow[currentWindow.length - 1];
      const rangeLabel = startLabel === endLabel ? startLabel : `${startLabel} - ${endLabel}`;
      setDataRangeLabel(rangeLabel);

      setHasPrevious(windowStart.current > 0);
      setHasNext(windowStart.current + daysPerPage < allDays.length);
    } else {
      windowStart.current = 0;
      setCurrentDays([]);
      setDataRangeLabel('');
      setHasPrevious(false);
      setHasNext(false);
    }
  }, [allDays, paginationTrigger, daysPerPage]);

  // Get the maximum value for color scaling
  const maxValue = data.length > 0 ? Math.max(...data.map((d) => d.value)) : 0;
  const minValue = data.length > 0 ? Math.min(...data.map((d) => d.value)) : 0;

  // Function to get color intensity based on value
  const getColorIntensity = (value: number) => {
    if (maxValue === minValue) return 0.5;
    return (value - minValue) / (maxValue - minValue);
  };

  // Function to get background color based on intensity
  const getBackgroundColor = (value: number) => {
    const intensity = getColorIntensity(value);

    // Using the same gradient colors as the legend
    // #FDEDED (0%) -> #F8BFBF (20%) -> #F28C8C (40%) -> #E65C5C (60%) -> #D94343 (80%) -> #C83333 (100%)
    if (intensity <= 0.2) {
      // Interpolate between #FDEDED and #F8BFBF
      const localIntensity = intensity / 0.2;
      const r = Math.floor(253 - (253 - 248) * localIntensity);
      const g = Math.floor(237 - (237 - 191) * localIntensity);
      const b = Math.floor(237 - (237 - 191) * localIntensity);
      return `rgb(${r}, ${g}, ${b})`;
    } else if (intensity <= 0.4) {
      // Interpolate between #F8BFBF and #F28C8C
      const localIntensity = (intensity - 0.2) / 0.2;
      const r = Math.floor(248 - (248 - 242) * localIntensity);
      const g = Math.floor(191 - (191 - 140) * localIntensity);
      const b = Math.floor(191 - (191 - 140) * localIntensity);
      return `rgb(${r}, ${g}, ${b})`;
    } else if (intensity <= 0.6) {
      // Interpolate between #F28C8C and #E65C5C
      const localIntensity = (intensity - 0.4) / 0.2;
      const r = Math.floor(242 - (242 - 230) * localIntensity);
      const g = Math.floor(140 - (140 - 92) * localIntensity);
      const b = Math.floor(140 - (140 - 92) * localIntensity);
      return `rgb(${r}, ${g}, ${b})`;
    } else if (intensity <= 0.8) {
      // Interpolate between #E65C5C and #D94343
      const localIntensity = (intensity - 0.6) / 0.2;
      const r = Math.floor(230 - (230 - 217) * localIntensity);
      const g = Math.floor(92 - (92 - 67) * localIntensity);
      const b = Math.floor(92 - (92 - 67) * localIntensity);
      return `rgb(${r}, ${g}, ${b})`;
    } else {
      // Interpolate between #D94343 and #C83333
      const localIntensity = (intensity - 0.8) / 0.2;
      const r = Math.floor(217 - (217 - 200) * localIntensity);
      const g = Math.floor(67 - (67 - 51) * localIntensity);
      const b = Math.floor(67 - (67 - 51) * localIntensity);
      return `rgb(${r}, ${g}, ${b})`;
    }
  };

  // Find data for specific hour and day
  const findDataPoint = (hour: string, day: string) => {
    return data.find((d) => d.hour === hour && d.day === day);
  };

  // Calculate percentage for tooltip
  const calculatePercentage = (value: number) => {
    if (maxValue === 0) return '0%';
    return `${Math.round((value / maxValue) * 100)}%`;
  };

  // Handle mouse enter for tooltip
  const handleMouseEnter = (
    event: React.MouseEvent,
    hour: string,
    day: string,
    value: number,
    displayValue: string,
  ) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setTooltip({
      hour,
      day,
      value: displayValue,
      percentage: calculatePercentage(value),
      x: rect.left + rect.width / 2,
      y: rect.top - 10,
    });
  };

  // Handle mouse leave for tooltip
  const handleMouseLeave = () => {
    setTooltip(null);
  };

  const handelTable = () => {
    if (onTableIconClick) {
      onTableIconClick(selectedAlert);
    }
    setIsTableOpen(true);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleExpand = () => {
    console.log('Expand clicked for:', baseTitle);
    setIsExpandedModalOpen(true);
    handleMenuClose();
  };

  const handleEditData = () => {
    console.log('Edit Data clicked for:', baseTitle);
    setIsEditModalOpen(true);
    handleMenuClose();
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
  };

  const handleApplyEditData = (data: any) => {
    console.log('Applied edit data:', data);
    // Call the onCardDataUpdate callback to refresh the chart data
    if (onCardDataUpdate) {
      onCardDataUpdate(chartId, data.startDate, data.endDate);
      // Show toast message
      const startDateFormatted = new Date(data.startDate).toLocaleDateString('en-GB');
      const endDateFormatted = new Date(data.endDate).toLocaleDateString('en-GB');
      showToast(`Date range applied: ${startDateFormatted} to ${endDateFormatted}`, 'success');
    }
  };

  const handleResetChanges = () => {
    console.log('Reset Changes clicked for:', baseTitle);
    handleMenuClose();
    setIsEditModalOpen(false);
    setResetTrigger((prev) => prev + 1);
    // Reset to last 7 days
    if (onCardDataUpdate) {
      const endDate = new Date().toISOString().split('T')[0];
      const startDate = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      onCardDataUpdate(chartId, startDate, endDate);
    }
  };

  const handleCloseExpandedModal = () => {
    setIsExpandedModalOpen(false);
  };

  return (
    <Box className="w-full h-full flex flex-col border rounded-md p-4 gap-2 items-start bg-white">
      {/* Header */}
      <Box className="flex items-center justify-between w-full mb-4">
        <Box className="flex items-center gap-2">
          <Typography
            variant="h6"
            fontSize={18}
            sx={(theme) => ({ color: theme.palette.text.primary, fontWeight: 600 })}
          >
            {title}
          </Typography>
          {infoIcon && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: 16,
                height: 16,
              }}
            >
              {infoIcon}
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, height: '40px' }}>
          {selectionBox && alertTypes.length > 0 && (
            <CustomDropDownInput
              options={[
                { value: '', label: 'All Alerts' },
                ...alertTypes.map((alertType: string) => ({
                  value: alertType,
                  label: alertType,
                })),
              ]}
              value={selectedAlert || ''}
              onChange={(value) => setSelectedAlert(value || null)}
              showDropdownArrow
              customStyles={{
                width: 'auto',
                height: '40px',
                borderRadius: '8px',
                border: '1px solid',
                borderColor: theme.palette.primary.main,
                minWidth: 'auto',
                '&:hover': {
                  background: '#FFFFFF',
                },
                typography: {
                  fontWeight: 700,
                  fontSize: '14px',
                  lineHeight: '20px',
                  color: theme.palette.primary.main,
                  whiteSpace: 'nowrap',
                },
                iconColor: theme.palette.primary.main,
              }}
            />
          )}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.3,
              cursor: 'pointer',
              height: '40px',
              flexShrink: 0,
              pt: '10px',
            }}
            onClick={handelTable}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 24,
                height: 24,
                flexShrink: 0,
              }}
            >
              <img src={eyeIcon} alt="Table" width={24} height={24} style={{ display: 'block' }} />
            </Box>
            <Typography
              variant="body2"
              fontSize={14}
              sx={(theme) => ({
                color: theme.palette.primary.main,
                display: 'flex',
                alignItems: 'center',
                height: '40px',
              })}
            >
              Table
            </Typography>
          </Box>
          <Box className="flex items-center gap-1 cursor-pointer" sx={{ pt: '10px' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 24,
                height: 24,
                flexShrink: 0,
              }}
            >
              <IconButton
                sx={{
                  p: 0,
                  width: '24px !important',
                  height: '24px !important',
                  minWidth: '24px !important',
                  minHeight: '24px !important',
                  maxWidth: '24px !important',
                  maxHeight: '24px !important',
                  flexShrink: 0,
                  display: 'flex !important',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                }}
                onClick={handleMenuClick}
              >
                <img
                  src={menuIcon}
                  alt="Menu"
                  width="24"
                  height="24"
                  style={{
                    display: 'block',
                    width: '24px',
                    height: '24px',
                    minWidth: '24px',
                    minHeight: '24px',
                  }}
                />
              </IconButton>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Menu Dropdown */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            borderRadius: '8px',
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
            minWidth: '160px',
          },
        }}
      >
        <MenuItem onClick={handleExpand} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}>
          <img src={OpenInFullIcon} alt="Expand" style={{ width: 24, height: 24 }} />
          <Typography sx={{ fontSize: '14px', color: '#333' }}>Expand</Typography>
        </MenuItem>
        <MenuItem onClick={handleEditData} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}>
          <img src={EditIcon} alt="Edit" style={{ width: 20, height: 20 }} />
          <Typography sx={{ fontSize: '14px', color: '#333' }}>Edit Data</Typography>
        </MenuItem>
        <MenuItem
          onClick={handleResetChanges}
          sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}
        >
          <img
            src={RestartAltIcon}
            alt="Reset"
            style={{
              width: 20,
              height: 20,
              filter:
                'invert(34%) sepia(68%) saturate(2449%) hue-rotate(338deg) brightness(91%) contrast(84%)',
            }}
          />
          <Typography sx={{ fontSize: '14px', color: '#DC3545' }}>Reset Changes</Typography>
        </MenuItem>
      </Menu>

      {/* Heatmap */}
      <Box className="w-full relative">
        {/* Color scale legend with min-max labels */}
        {data.length > 0 && (
          <Box className="flex items-center justify-end mb-2 gap-2">
            <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '12px', fontWeight: 400 }}>
              {minValue}
            </Typography>
            <Box
              sx={{
                width: '180px',
                height: '12px',
                borderRadius: '8px',
                background:
                  'linear-gradient(90deg, #FDEDED 0%, #F8BFBF 20%, #F28C8C 40%, #E65C5C 60%, #D94343 80%, #C83333 100%)',
              }}
            />
            <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '12px', fontWeight: 400 }}>
              {maxValue}
            </Typography>
          </Box>
        )}

        {/* No Data Message */}
        {data.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '300px',
              width: '100%',
            }}
          >
            <img src={NoDataIcon} alt="No data" className="w-48 h-48 opacity-50" />
          </Box>
        ) : (
          <>
            {/* Main container with Hours label */}
            <Box className="flex items-center">
              {/* Hours label on the left */}
              <Box className="mr-1">
                <Typography
                  variant="caption"
                  sx={{
                    transform: 'rotate(-90deg)',
                    fontWeight: 400,
                    fontSize: '12px',
                    lineHeight: '18px',
                    letterSpacing: '0%',
                    textAlign: 'center',
                    color: '#1B1B1B',
                    whiteSpace: 'nowrap',
                    display: 'block',
                    width: '10px',
                  }}
                >
                  Hours
                </Typography>
              </Box>

              {/* Grid Container */}
              <Box className="flex-1">
                <Box
                  className="gap-1"
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: `auto repeat(${currentDays.length}, 1fr)`, // auto for hour labels column
                    gridTemplateRows: `repeat(${hours.length}, 1fr)`, // Dynamic rows based on hours
                    alignItems: 'center',
                  }}
                >
                  {/* Data rows - hours on left, data cells for each day */}
                  {hours.map((hour, index) => (
                    <React.Fragment key={hour || `empty-${index}`}>
                      {/* Hour label on the left */}
                      <Box
                        className="p-2 text-center flex items-center justify-start"
                        sx={{
                          backgroundColor: 'transparent',
                          border: 'none',
                          fontWeight: 400,
                          fontSize: '12px',
                          lineHeight: '18px',
                          letterSpacing: '0%',
                          color: '#767676',
                          minWidth: '60px',
                        }}
                      >
                        {hour}
                      </Box>

                      {/* Data cells for each day */}
                      {currentDays.map((day) => {
                        const dataPoint = findDataPoint(hour, day);
                        const value = dataPoint?.value || 0;
                        const displayValue = dataPoint?.displayValue || '0';

                        return (
                          <Box
                            key={`${hour}-${day}`}
                            className="p-2 text-center flex items-center justify-center"
                            sx={{
                              backgroundColor: getBackgroundColor(value),
                              border: '1px solid #E5E7EB',
                              borderRadius: '5px',
                              fontSize: '11px',
                              fontWeight: 600,
                              color: getColorIntensity(value) > 0.5 ? 'white' : '#374151',
                              minHeight: 32,
                              cursor: 'pointer',
                              '&:hover': {
                                opacity: 0.8,
                                transform: 'scale(1.05)',
                                transition: 'all 0.2s ease-in-out',
                              },
                            }}
                            onMouseEnter={(e) => handleMouseEnter(e, hour, day, value, displayValue)}
                            onMouseLeave={handleMouseLeave}
                          >
                            {displayValue}
                          </Box>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </Box>

                {/* Bottom row with days */}
                <Box
                  className="gap-1 mt-1"
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: `auto repeat(${currentDays.length}, 1fr)`,
                  }}
                >
                  <Box sx={{ minWidth: '60px' }}></Box> {/* Empty cell for hour labels column */}
                  {currentDays.map((day) => (
                    <Box
                      key={day}
                      className="p-2 text-center"
                      sx={{
                        backgroundColor: 'transparent',
                        border: 'none',
                        fontWeight: 400,
                        fontSize: '12px',
                        lineHeight: '18px',
                        letterSpacing: '0%',
                        color: '#767676',
                      }}
                    >
                      {day}
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>

            {/* Days label at bottom */}
            <Box className="text-center mt-2">
              <Typography variant="caption" sx={{ color: '#1B1B1B', fontSize: '12px', fontWeight: 400 }}>
                Days
              </Typography>
            </Box>

            {/* Pagination Controls */}
            {allDays.length > daysPerPage && (
              <Box className="flex justify-center items-center mt-4 gap-4">
                <IconButton
                  size="small"
                  onClick={handlePrevious}
                  disabled={!hasPrevious}
                  sx={{
                    border: '1px solid #5547DD',
                    borderRadius: '8px',
                    width: '32px',
                    height: '32px',
                    '&:disabled': {
                      opacity: 0.5,
                    },
                  }}
                >
                  <img src={ArrowLeftIcon} alt="Previous" style={{ width: '16px', height: '16px' }} />
                </IconButton>

                <Typography variant="caption" color="textPrimary" align="center">
                  {dataRangeLabel}
                </Typography>

                <IconButton
                  size="small"
                  onClick={handleNext}
                  disabled={!hasNext}
                  sx={{
                    border: '1px solid #5547DD',
                    borderRadius: '8px',
                    width: '32px',
                    height: '32px',
                    '&:disabled': {
                      opacity: 0.5,
                    },
                  }}
                >
                  <img src={ArrowRightIcon} alt="Next" style={{ width: '16px', height: '16px' }} />
                </IconButton>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Tooltip */}
      {tooltip && (
        <Box
          sx={{
            position: 'fixed',
            left: tooltip.x,
            top: tooltip.y,
            transform: 'translateX(-50%) translateY(-100%)',
            backgroundColor: 'white',
            color: 'black',
            padding: '8px 12px',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: 500,
            zIndex: 1000,
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            border: '1px solid #E5E7EB',
          }}
        >
          <Box sx={{ marginBottom: '4px', fontSize: '11px', fontWeight: 600, color: 'black' }}>
            {tooltip.hour}, {tooltip.day}
          </Box>
          <Box sx={{ fontSize: '13px', fontWeight: 700, color: 'black' }}>
            {tooltip.value} ({tooltip.percentage})
          </Box>
        </Box>
      )}
      <Modal
        open={isTableOpen}
        onClose={() => setIsTableOpen(false)}
        aria-labelledby="alert-table-modal"
        aria-describedby="alert-table-description"
      >
        <Box
          sx={{
            position: 'absolute' as const,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            maxWidth: '90vw',
            maxHeight: '90vh',
            width: 'auto',
            height: '90vh',
            bgcolor: 'background.paper',
            borderRadius: '8px',
            boxShadow: 24,
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {tableColumns && tableColumns.length > 0 ? (
            <CommonAnalysisTable
              title={tableTitle}
              headerText={tableHeaderText}
              columns={tableColumns}
              data={tableData}
              paginationInfo={paginationInfo}
              onPaginationChange={onPaginationChange}
              noDataMessage="No data available for this selection"
              onClose={() => setIsTableOpen(false)}
            />
          ) : (
            <NumericalAnalysisTable />
          )}
        </Box>
      </Modal>

      {/* Expanded Heatmap Modal */}
      <Modal
        open={isExpandedModalOpen}
        onClose={handleCloseExpandedModal}
        aria-labelledby="expanded-heatmap-modal"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: { xs: '90%', sm: '85%', md: '80%', lg: '80%' },
            maxWidth: { xs: 525, sm: 840, md: 1050, lg: 1155 },
            maxHeight: { xs: '85vh', sm: '90vh', md: '95vh' },
            bgcolor: 'white',
            borderRadius: '16px',
            boxShadow: 24,
            p: { xs: 2, sm: 2.5, md: 3 },
            outline: 'none',
            overflow: 'visible',
          }}
        >
          {/* Modal Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              mb: { xs: 1.5, sm: 2 },
              position: 'relative',
              zIndex: 10,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontStyle: 'normal',
                  fontSize: { xs: '18px', sm: '24px', md: '28px' },
                  lineHeight: { xs: '27px', sm: '36px', md: '42px' },
                  letterSpacing: '0%',
                  color: '#1B1B1B',
                }}
              >
                {title}
              </Typography>
              {infoIcon && (
                <Box sx={{ display: 'flex', alignItems: 'center', width: 16, height: 16 }}>{infoIcon}</Box>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Edit Chart Button */}
              <Box
                onClick={handleEditData}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  padding: '8px 12px',
                  borderRadius: '8px',
                  colour: '#5547DD',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(85, 71, 221, 0.1)',
                  },
                }}
              >
                <img src={EditIcon} alt="Edit" style={{ width: 16, height: 16 }} />
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontStyle: 'normal',
                    fontSize: '14px',
                    lineHeight: '21px',
                    letterSpacing: '0%',
                    color: '#5547DD',
                  }}
                >
                  Edit Chart
                </Typography>
              </Box>

              {/* Collapse Button */}
              <Box
                onClick={handleCloseExpandedModal}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  padding: '8px 12px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(85, 71, 221, 0.1)',
                  },
                }}
              >
                <img src={CollapseIcon} alt="Collapse" style={{ width: 16, height: 16 }} />
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontStyle: 'normal',
                    fontSize: '14px',
                    lineHeight: '21px',
                    letterSpacing: '0%',
                    color: '#5547DD',
                  }}
                >
                  Collapse
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Expanded Heatmap Container with Scroll */}
          <Box
            sx={{
              width: '100%',
              mt: { xs: 2, sm: 2.5, md: 3 },
              position: 'relative',
              overflowY: 'auto',
              overflowX: 'visible',
              maxHeight: 'calc(85vh - 120px)',
              paddingRight: '8px',
            }}
          >
            {/* Color scale legend with min-max labels */}
            {data.length > 0 && (
              <Box className="flex items-center justify-end mb-4 gap-2">
                <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '12px', fontWeight: 400 }}>
                  {minValue}
                </Typography>
                <Box
                  sx={{
                    width: '240px',
                    height: '16px',
                    borderRadius: '8px',
                    background:
                      'linear-gradient(90deg, #FDEDED 0%, #F8BFBF 20%, #F28C8C 40%, #E65C5C 60%, #D94343 80%, #C83333 100%)',
                  }}
                />
                <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '12px', fontWeight: 400 }}>
                  {maxValue}
                </Typography>
              </Box>
            )}

            {/* No Data Message */}
            {data.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: '400px',
                  width: '100%',
                }}
              >
                <img src={NoDataIcon} alt="No data" className="w-48 h-48 opacity-50" />
              </Box>
            ) : (
              <>
                {/* Main container with Hours label */}
                <Box className="flex items-center">
                  {/* Hours label on the left */}
                  <Box className="mr-2">
                    <Typography
                      variant="caption"
                      sx={{
                        transform: 'rotate(-90deg)',
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '20px',
                        letterSpacing: '0%',
                        textAlign: 'center',
                        color: '#1B1B1B',
                        whiteSpace: 'nowrap',
                        display: 'block',
                        width: '14px',
                      }}
                    >
                      Hours
                    </Typography>
                  </Box>

                  {/* Grid Container */}
                  <Box className="flex-1">
                    <Box
                      className="gap-2"
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: `auto repeat(${currentDays.length}, 1fr)`,
                        gridTemplateRows: `repeat(${hours.length}, 1fr)`,
                        alignItems: 'center',
                      }}
                    >
                      {hours.map((hour, index) => (
                        <React.Fragment key={hour || `empty-${index}`}>
                          {/* Hour label on the left */}
                          <Box
                            className="p-2 text-center flex items-center justify-start"
                            sx={{
                              backgroundColor: 'transparent',
                              border: 'none',
                              fontWeight: 400,
                              fontSize: '12px',
                              lineHeight: '18px',
                              letterSpacing: '0%',
                              color: '#767676',
                              minWidth: '60px',
                            }}
                          >
                            {hour}
                          </Box>

                          {/* Data cells for each day */}
                          {currentDays.map((day) => {
                            const dataPoint = findDataPoint(hour, day);
                            const value = dataPoint?.value || 0;
                            const displayValue = dataPoint?.displayValue || '0';

                            return (
                              <Box
                                key={`${hour}-${day}`}
                                className="p-2 text-center flex items-center justify-center"
                                sx={{
                                  backgroundColor: getBackgroundColor(value),
                                  border: '1px solid #E5E7EB',
                                  borderRadius: '5px',
                                  fontSize: '12px',
                                  fontWeight: 600,
                                  color: getColorIntensity(value) > 0.5 ? 'white' : '#374151',
                                  minHeight: 40,
                                  cursor: 'pointer',
                                  '&:hover': {
                                    opacity: 0.8,
                                    transform: 'scale(1.05)',
                                    transition: 'all 0.2s ease-in-out',
                                  },
                                }}
                                onMouseEnter={(e) => handleMouseEnter(e, hour, day, value, displayValue)}
                                onMouseLeave={handleMouseLeave}
                              >
                                {displayValue}
                              </Box>
                            );
                          })}
                        </React.Fragment>
                      ))}
                    </Box>

                    {/* Bottom row with days */}
                    <Box
                      className="gap-2 mt-2"
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: `auto repeat(${currentDays.length}, 1fr)`,
                      }}
                    >
                      <Box sx={{ minWidth: '60px' }}></Box>
                      {currentDays.map((day) => (
                        <Box
                          key={day}
                          className="p-2 text-center"
                          sx={{
                            backgroundColor: 'transparent',
                            border: 'none',
                            fontWeight: 400,
                            fontSize: '12px',
                            lineHeight: '18px',
                            letterSpacing: '0%',
                            color: '#767676',
                          }}
                        >
                          {day}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>

                {/* Days label at bottom */}
                <Box className="text-center mt-4">
                  <Typography variant="caption" sx={{ color: '#1B1B1B', fontSize: '12px', fontWeight: 400 }}>
                    Days
                  </Typography>
                </Box>

                {/* Pagination Controls */}
                {allDays.length > daysPerPage && (
                  <Box className="flex justify-center items-center mt-6 gap-2">
                    <IconButton
                      size="small"
                      onClick={handlePrevious}
                      disabled={!hasPrevious}
                      sx={{
                        border: '1px solid #5547DD',
                        borderRadius: '8px',
                        width: '40px',
                        height: '40px',
                        '&:disabled': {
                          opacity: 0.5,
                        },
                      }}
                    >
                      <img src={ArrowLeftIcon} alt="Previous" style={{ width: '20px', height: '20px' }} />
                    </IconButton>

                    <Typography variant="body2" color="textPrimary" align="center" sx={{ minWidth: '120px' }}>
                      {dataRangeLabel}
                    </Typography>

                    <IconButton
                      size="small"
                      onClick={handleNext}
                      disabled={!hasNext}
                      sx={{
                        border: '1px solid #5547DD',
                        borderRadius: '8px',
                        width: '40px',
                        height: '40px',
                        '&:disabled': {
                          opacity: 0.5,
                        },
                      }}
                    >
                      <img src={ArrowRightIcon} alt="Next" style={{ width: '20px', height: '20px' }} />
                    </IconButton>
                  </Box>
                )}
              </>
            )}
          </Box>

          {/* Tooltip for Expanded Modal */}
          {tooltip && (
            <Box
              sx={{
                position: 'fixed',
                left: tooltip.x,
                top: tooltip.y,
                transform: 'translateX(-50%) translateY(-100%)',
                backgroundColor: 'white',
                color: 'black',
                padding: '8px 12px',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: 500,
                zIndex: 10000,
                pointerEvents: 'none',
                whiteSpace: 'nowrap',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                border: '1px solid #E5E7EB',
              }}
            >
              <Box sx={{ marginBottom: '4px', fontSize: '11px', fontWeight: 600, color: 'black' }}>
                {tooltip.hour}, {tooltip.day}
              </Box>
              <Box sx={{ fontSize: '13px', fontWeight: 700, color: 'black' }}>
                {tooltip.value} ({tooltip.percentage})
              </Box>
            </Box>
          )}
        </Box>
      </Modal>

      {/* Edit Data Modal */}
      <EditDataModal
        open={isEditModalOpen}
        onClose={handleCloseEditModal}
        onApply={handleApplyEditData}
        onReset={() => console.log('Reset clicked')}
        cardTitle={baseTitle}
        chartId={chartId}
        resetTrigger={resetTrigger}
      />
    </Box>
  );
};
export default CommonHeatmapChart;
